import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileDigit, Clock, AlertCircle, CheckCircle, ExternalLink, Key } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerDownloads() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDownloads = async () => {
      try {
        const res = await api.get('/customer/downloads');
        setDownloads(res.data.downloads || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchDownloads();
  }, []);

  const handleDownload = async (token) => {
    try {
      const res = await api.get(`/download/${token}`);
      if (res.data.download_url) {
        window.open(res.data.download_url, '_blank');
        toast.success('Download dimulai...');
        // Refresh list
        const updated = await api.get('/customer/downloads');
        setDownloads(updated.data.downloads || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal download');
    }
  };

  const getStatusBadge = (download) => {
    if (!download.is_valid) {
      if (download.status === 'expired') {
        return <Badge className="bg-red-100 text-red-700"><Clock size={12} className="mr-1" /> Expired</Badge>;
      }
      return <Badge className="bg-amber-100 text-amber-700"><AlertCircle size={12} className="mr-1" /> Limit Reached</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle size={12} className="mr-1" /> Active</Badge>;
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
      </div>
    );
  }

  return (
    <div data-testid="customer-downloads-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Produk Digital
        </h1>
        <p className="text-sm text-slate-500">Kelola dan download produk digital Anda</p>
      </div>

      {downloads.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <FileDigit size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Belum ada produk digital</h3>
            <p className="text-sm text-slate-500">Produk digital yang Anda beli akan muncul di sini</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {downloads.map((d) => (
            <Card key={d.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg flex items-center justify-center">
                      <FileDigit size={24} className="text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {d.product?.name || d.product_name}
                        {d.file_label && d.file_label !== 'Download' && (
                          <span className="ml-2 text-xs font-normal text-slate-500">— {d.file_label}</span>
                        )}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span>Order: {new Date(d.created_at).toLocaleDateString('id-ID')}</span>
                        {d.download_limit !== -1 && (
                          <span>Download: {d.download_count}/{d.download_limit}</span>
                        )}
                        {d.expires_at && (
                          <span>Expired: {new Date(d.expires_at).toLocaleDateString('id-ID')}</span>
                        )}
                      </div>
                      {d.serial_key && (
                        <div className="flex items-center gap-2 mt-2">
                          <Key size={14} className="text-violet-500" />
                          <span className="text-sm font-medium text-violet-700">Serial Key:</span>
                          <code className="bg-violet-50 text-violet-800 px-2 py-0.5 rounded text-sm font-mono">{d.serial_key}</code>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(d)}
                    <Button
                      onClick={() => handleDownload(d.token)}
                      disabled={!d.is_valid}
                      className={d.is_valid ? 'bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]' : 'bg-slate-200 text-slate-500'}
                      data-testid={`download-btn-${d.id}`}
                    >
                      <Download size={16} className="mr-2" /> Download
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
